import { ApolloError } from '@apollo/client';
import * as React from 'react';
import { useHistory } from 'react-router';
import Cookies from 'universal-cookie';
import { AppContext } from '../AppContext';
import { Button } from './Button';
import './Dialogs.css';

interface IDialogContainerProps {
  open: boolean;
  message: React.ReactElement<any, any>;
  title?: string;
}

export const DialogContainer: React.FC<IDialogContainerProps> = function (props) {
  return (
    <div className="Dialog-background">
      <div className="Dialog">
        <div className="Dialog-main">
          <p className="Dialog-title">{props.title}</p>
          {props.message}
        </div>
        <div className="Dialog-button_container">{props.children}</div>
      </div>
    </div>
  );
};

interface INotifyDialogProps extends IDialogContainerProps {
  yesLabel?: string;
  onYes: () => void;
}

export const NotifyDialog: React.FC<INotifyDialogProps> = function (props) {
  if (!props.open) {
    return <div></div>;
  }

  return (
    <DialogContainer {...props}>
      <Button onClick={() => props.onYes?.()}>{props.yesLabel ?? 'OK'}</Button>
    </DialogContainer>
  );
};

interface IConfirmDialogProps extends IDialogContainerProps {
  yesLabel?: string;
  noLabel?: string;
  onYes: () => void;
  onNo?: () => void;
}

export const ConfirmDialog: React.FC<IConfirmDialogProps> = function (props) {
  if (!props.open) {
    return <div></div>;
  }

  return (
    <DialogContainer {...props}>
      <Button onClick={() => props.onYes?.()}>
        {props.yesLabel ?? 'OK'}
      </Button>
      <Button
        color="white"
        onClick={() => props.onNo?.()}
      >
        {props.yesLabel ?? 'キャンセル'}
      </Button>
    </DialogContainer>
  );
};

interface IFatalErrorDialogProps { }

export const FatalErrorDialog: React.FC<IFatalErrorDialogProps> = function (
  props
) {
  return (
    <DialogContainer
      {...props}
      title="エラー"
      message={<p>エラーが発生しました</p>}
      open={true}
    >
      <Button onClick={() => window.location.reload()}>
        再読み込み
      </Button>
    </DialogContainer>
  );
};


interface IApiErrorDialogProps {
  error: Error;
}

export const ApiErrorDialog: React.FC<IApiErrorDialogProps> = function (
  props
) {
  return (
    <DialogContainer
      {...props}
      title="通信エラー"
      message={<p>{props.error.message}</p>}
      open={true}
    >
      <Button
        onClick={() => window.location.reload()}>
        再読み込み
      </Button>
    </DialogContainer>
  );
};

interface IGraphQLErrorDialogProps {
  error: ApolloError;
}

export const GraphQLErrorDialog: React.FC<IGraphQLErrorDialogProps> = function (
  props
) {
  return (
    <DialogContainer
      {...props}
      title="サーバーエラー"
      message={<p>{props.error.message}</p>}
      open={true}
    >
      <Button onClick={() => window.location.reload()}>
        再読み込み
      </Button>
    </DialogContainer>
  );
};

interface IGraphQLMutationErrorDialogProps {
  error: ApolloError;
  onOk?: () => void;
}

export const GraphQLMutationErrorDialog: React.FC<IGraphQLMutationErrorDialogProps> = function (
  props
) {
  const [open, setOpen] = React.useState(true);

  return (
    <DialogContainer
      title="サーバーエラー"
      message={<p>エラーが発生しました。時間をおいて再実行してください。</p>}
      open={open}
    >
      <Button
        onClick={() => {
          setOpen(false);
          props.onOk?.();
        }}
      >
        OK
      </Button>
    </DialogContainer>
  );
};

interface ISvgNameInputDialogProps {
  open: boolean;
  svgItems: { fileName: string; svg: string }[];
  onConfirm: (items: { name: string; svg: string }[]) => void;
  onCancel: () => void;
  editMode?: boolean;
}

export const SvgNameInputDialog: React.FC<ISvgNameInputDialogProps> = function (props) {
  // ファイル名から拡張子を除去してデフォルト名として使用
  const getDefaultName = (fileName: string) => {
    return fileName.replace(/\.[^/.]+$/, '');
  };

  // 編集モードの場合はfileNameをそのまま初期値として使用
  const getInitialNames = () => {
    return props.svgItems.map(item =>
      props.editMode ? item.fileName : ''
    );
  };

  const [names, setNames] = React.useState<string[]>(getInitialNames);

  // svgItemsが変更されたらnamesをリセット
  React.useEffect(() => {
    setNames(getInitialNames());
  }, [props.svgItems, props.editMode]);

  if (!props.open || props.svgItems.length === 0) {
    return null;
  }

  const handleNameChange = (index: number, value: string) => {
    const newNames = [...names];
    newNames[index] = value;
    setNames(newNames);
  };

  const handleConfirm = () => {
    const result = props.svgItems.map((item, index) => ({
      name: names[index] || getDefaultName(item.fileName),
      svg: item.svg,
    }));
    props.onConfirm(result);
  };

  return (
    <div className="Dialog-background">
      <div className="Dialog SvgNameInputDialog">
        <div className="Dialog-main">
          <p className="Dialog-title">{props.editMode ? 'シンボル名を編集' : 'シンボル名を入力'}</p>
          <div className="SvgNameInputDialog-list">
            {props.svgItems.map((item, index) => (
              <div key={index} className="SvgNameInputDialog-row">
                <div className="SvgNameInputDialog-svg">
                  <img
                    src={`data:image/svg+xml;charset=utf-8,${encodeURIComponent(item.svg).replace(/'/g, "%27").replace(/"/g, "%22")}`}
                    alt={item.fileName}
                  />
                </div>
                <input
                  type="text"
                  className="SvgNameInputDialog-input"
                  placeholder={props.editMode ? '' : getDefaultName(item.fileName)}
                  value={names[index]}
                  onChange={(e) => handleNameChange(index, e.target.value)}
                />
              </div>
            ))}
          </div>
        </div>
        <div className="Dialog-button_container">
          <Button onClick={handleConfirm}>{props.editMode ? '保存' : '追加'}</Button>
          <Button color="white" onClick={props.onCancel}>キャンセル</Button>
        </div>
      </div>
    </div>
  );
};

interface ISvgDeleteConfirmDialogProps {
  open: boolean;
  svg: string;
  name?: string;
  onConfirm: () => void;
  onCancel: () => void;
}

export const SvgDeleteConfirmDialog: React.FC<ISvgDeleteConfirmDialogProps> = function (props) {
  if (!props.open) {
    return null;
  }

  return (
    <div className="Dialog-background">
      <div className="Dialog SvgDeleteConfirmDialog">
        <div className="Dialog-main">
          <p className="Dialog-title">削除の確認</p>
          <p className="SvgDeleteConfirmDialog-message">このSVGを削除しますか？</p>
          <div className="SvgDeleteConfirmDialog-preview">
            <div className="SvgDeleteConfirmDialog-svg">
              <img
                src={`data:image/svg+xml;charset=utf-8,${encodeURIComponent(props.svg).replace(/'/g, "%27").replace(/"/g, "%22")}`}
                alt="削除対象のSVG"
              />
            </div>
            {props.name && (
              <div className="SvgDeleteConfirmDialog-name">{props.name}</div>
            )}
          </div>
        </div>
        <div className="Dialog-button_container">
          <Button onClick={props.onConfirm}>削除</Button>
          <Button color="white" onClick={props.onCancel}>キャンセル</Button>
        </div>
      </div>
    </div>
  );
};
